import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { translate } from '../../Services/translation';
import { get } from '../../Services/http';
import { numberFormat } from '../../Services/Formatting.service';

import ConfiguratorOptionStep from './ConfiguratorOptionStep';
import ConfiguratorAccessoryGroup from './ConfiguratorAccessoryGroup';
import ConfiguratorProductCard from './ConfiguratorProductCard';
import ConfiguratorPopUp from './ConfiguratorPopUp';
import ConfiguratorSummary from './ConfiguratorSummary';
import {
  fieldValuesEqual,
  ACCESSORIES_PARAM_KEY,
  SUMMARY_STEP,
  accessorySectionKey,
  buildConfiguratorSearchParams,
  fieldSectionKey,
  filterProductsByFilters,
  isOptionSkippable,
  parseAccessoriesParam,
  pickProductId,
  readConfiguratorUrlState,
  stepFromSectionKey,
} from '../../Services/Configurator.service';

const isUrlStateComplete = (opts, urlState) =>
  opts.length > 0 &&
  urlState.firstUnansweredFieldId == null &&
  urlState.isSummaryStep;

// CMS content boxes (RenderAsContentBoxes) are manually authored per-value; a missing title/fieldValue
// renders as an empty, unselectable box with no visible error, so surface it in the console instead.
const warnAboutIncompleteOptions = (options) => {
  (options || []).forEach((option) => {
    const incompleteValues = (option.values || []).filter(
      (value) => value.fieldValue == null || value.title == null
    );
    if (incompleteValues.length > 0) {
      console.warn(
        `[Configurator] Option "${option.fieldId}" (renderer: ${option.renderer}) has ${incompleteValues.length} of ${option.values.length} value(s) missing title/fieldValue.`,
        incompleteValues
      );
    }
  });
};

const parsePrice = (price) => {
  if (typeof price === 'number') return price;
  if (!price) return 0;
  const cleaned = price.toString().replace(/[^0-9.,]/g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
};

const ConfiguratorContainer = ({ channelId, pageId, inquiryInfo, tosText }) => {
  const [options, setOptions] = useState([]);
  const [products, setProducts] = useState([]);
  const [openSectionKey, setOpenSectionKey] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({});
  // Article number of the exact product - a field combination can match several products
  // (e.g. a skipped field), so the product can't be re-derived from the filters alone.
  const [pinnedProductId, setPinnedProductId] = useState(null);
  const [accessoryGroups, setAccessoryGroups] = useState([]);
  const [selectedAccessories, setSelectedAccessories] = useState([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [pendingResetIndex, setPendingResetIndex] = useState(null);
  const [isMobileSummaryOpen, setIsMobileSummaryOpen] = useState(false);
  const [urlError, setUrlError] = useState(false);
  // Bumped by user actions; the URL is pushed from an effect so it reflects the committed state.
  const [historyVersion, setHistoryVersion] = useState(0);
  const [copied, setCopied] = useState(false);

  // "accessories" URL param waiting for the matching product's accessory groups to load.
  const pendingAccessoriesRawRef = useRef(
    new URLSearchParams(window.location.search).get(ACCESSORIES_PARAM_KEY)
  );
  // Section toggles only change which step is shown, so they replace the history entry instead of adding one.
  const replaceHistoryRef = useRef(false);
  // Set by actions that replace the focused control (next, edit, back), so keyboard focus follows the step
  // they open. Page loads, history navigation and header toggles leave focus where it is.
  const focusOpenedStepRef = useRef(false);
  // Child effects run before this one, so the opened step has already read the flag when it is cleared.
  useEffect(() => {
    focusOpenedStepRef.current = false;
  });
  const accessoryGroupsRef = useRef(accessoryGroups);
  const selectedProductIdRef = useRef(null);

  const hasProductsLoaded = products.length > 0;
  const filteredProducts = filterProductsByFilters(products, selectedFilters);
  // With no filters every product vacuously matches, so nothing is selected until a field is answered.
  const selectedProduct =
    Object.keys(selectedFilters).length > 0
      ? filteredProducts.find((p) => p.id === pinnedProductId) ||
        filteredProducts[0] ||
        null
      : null;
  const selectedProductId = selectedProduct?.id ?? null;
  const productPrice = parsePrice(selectedProduct?.price);
  const accessoriesTotalPrice = selectedAccessories.reduce((sum, item) => {
    const itemPrice = parsePrice(item.accessory?.price);
    return sum + itemPrice * (item.quantity || 1);
  }, 0);
  const totalPrice = productPrice + accessoriesTotalPrice;

  // Handle if we have more than 1 matching product when the user has answered all options, so they can pick the one they want and select accessories
  const isAccessoryStep =
    openSectionKey?.startsWith('accessory:') ||
    selectedAccessories.length > 0;
  const lastOptionFieldId = options[options.length - 1]?.fieldId;
  const isLastOptionStep = openSectionKey === fieldSectionKey(lastOptionFieldId);
  const canSelectProduct = isLastOptionStep && filteredProducts.length > 1;
  const displayProducts = isAccessoryStep && selectedProduct
    ? [selectedProduct]
    : filteredProducts;

  useEffect(() => {
    accessoryGroupsRef.current = accessoryGroups;
  }, [accessoryGroups]);

  useEffect(() => {
    selectedProductIdRef.current = selectedProductId;
  }, [selectedProductId]);

  const pushConfiguratorUrl = (filters, accessories, productId) => {
    const searchParams = buildConfiguratorSearchParams(
      filters,
      accessories,
      productId,
      isCompleted ? SUMMARY_STEP : stepFromSectionKey(openSectionKey, options)
    );

    const search = searchParams.toString();
    const newUrl = `${window.location.pathname}${search ? `?${search}` : ''}${
      window.location.hash
    }`;
    if (replaceHistoryRef.current) {
      replaceHistoryRef.current = false;
      window.history.replaceState(null, '', newUrl);
    } else {
      window.history.pushState(null, '', newUrl);
    }
  };

  const readUrlState = (opts, prods) => {
    const urlState = readConfiguratorUrlState(
      new URLSearchParams(window.location.search),
      opts,
      prods
    );
    if (urlState.mismatch) {
      console.warn(
        "[Configurator] Pinned product from the URL doesn't match the selected fields; falling back to the first match.",
        {
          requestedProductId: urlState.requestedProductId,
          matchedProductIds: urlState.matchedProductIds,
        }
      );
    }
    return urlState;
  };

  const applyPendingAccessories = (groups) => {
    const raw = pendingAccessoriesRawRef.current;
    if (!raw || groups.length === 0) return;

    pendingAccessoriesRawRef.current = null;

    const { entries, hasError } = parseAccessoriesParam(raw);
    const allItems = groups.flatMap(
      (group) => group.items || group.accessories || []
    );

    const nextSelected = [];
    const missingIds = [];
    entries.forEach(({ id, quantity }) => {
      const accessory = allItems.find((item) => String(item.id) === id);
      if (accessory) {
        nextSelected.push({ accessory, quantity });
      } else {
        missingIds.push(id);
      }
    });

    setSelectedAccessories(nextSelected);
    if (hasError || missingIds.length > 0) {
      console.warn(
        '[Configurator] Some accessory ids from the URL were not found in the loaded accessory groups.',
        { missingIds, availableIds: allItems.map((item) => item.id) }
      );
      setUrlError(true);
    }
  };

  useEffect(() => {
    get('/api/configurator?imageWidth=800&imageHeight=600')
      .then((response) => response.json())
      .then((data) => {
        const opts = data.options || [];
        const prods = data.products || [];
        warnAboutIncompleteOptions(opts);
        const urlState = readUrlState(opts, prods);

        // React 17 doesn't batch outside event handlers; a render with products but no filters would
        // resolve the wrong product, fetch its accessories and consume the pending URL accessories.
        unstable_batchedUpdates(() => {
          setOptions(opts);
          setProducts(prods);
          setSelectedFilters(urlState.filters);
          setPinnedProductId(urlState.productId);
          setOpenSectionKey(urlState.openSectionKey);
          setIsCompleted(isUrlStateComplete(opts, urlState));
          if (urlState.hasError) setUrlError(true);
        });
      })
      .catch((ex) => console.log(ex));
  }, []);

  useEffect(() => {
    applyPendingAccessories(accessoryGroups);
  }, [accessoryGroups]);

  useEffect(() => {
    if (!selectedProductId) {
      setAccessoryGroups([]);
      // Nothing to attach the URL accessories to; drop them so a later manual pick doesn't inherit them.
      if (hasProductsLoaded) pendingAccessoriesRawRef.current = null;
      return undefined;
    }

    // Drop the previous product's groups so its accessories can't be added while loading.
    setAccessoryGroups([]);
    // Ignore responses for a product that is no longer selected.
    let isStale = false;
    get(
      `/api/configurator/accessories/${selectedProductId}?imageWidth=400&imageHeight=400`
    )
      .then((response) => response.json())
      .then((data) => {
        if (!isStale) {
          const groups = data || [];
          setAccessoryGroups(groups);
          // A step from the URL can point past the last accessory group.
          setOpenSectionKey((key) => {
            const index = key?.startsWith('accessory:')
              ? Number(key.split(':')[1])
              : null;
            if (index == null || index < groups.length) return key;
            return groups.length > 0 ? accessorySectionKey(groups.length - 1) : null;
          });
        }
      })
      .catch((ex) => console.log(ex));
    return () => {
      isStale = true;
    };
  }, [selectedProductId, hasProductsLoaded]);

  // Reflect browser back/forward navigation into the configurator state.
  useEffect(() => {
    if (options.length === 0) return undefined;

    const handlePopState = () => {
      const previousProductId = selectedProductIdRef.current;
      const urlState = readUrlState(options, products);

      unstable_batchedUpdates(() => {
        setSelectedFilters(urlState.filters);
        setPinnedProductId(urlState.productId);
        setUrlError(urlState.hasError);
        setOpenSectionKey(urlState.openSectionKey);
        setIsCompleted(isUrlStateComplete(options, urlState));
        pendingAccessoriesRawRef.current = urlState.rawAccessories;
        if (!urlState.rawAccessories) setSelectedAccessories([]);
      });

      // A changed product refetches its groups; the accessoryGroups effect applies the accessories then.
      if (urlState.rawAccessories && urlState.productId === previousProductId) {
        applyPendingAccessories(accessoryGroupsRef.current);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [options, products]);

  useEffect(() => {
    if (historyVersion === 0) return;
    pushConfiguratorUrl(
      selectedFilters,
      selectedAccessories,
      selectedProductId
    );
  }, [historyVersion]);

  // Any user-driven change supersedes whatever the URL asked for.
  const commitUserSelection = (nextFilters, nextAccessories) => {
    pendingAccessoriesRawRef.current = null;
    setSelectedFilters(nextFilters);
    setPinnedProductId(pickProductId(products, nextFilters, pinnedProductId));
    setSelectedAccessories(nextAccessories);
    setHistoryVersion((v) => v + 1);
  };

  const handleProductSelect = (product) => {
    setPinnedProductId(product.id);
    setHistoryVersion((v) => v + 1);
  };

  // Moving to another step gets its own history entry so the URL (and back button) follows the visitor.
  const navigateToSection = (sectionKey, moveFocus = true) => {
    focusOpenedStepRef.current = moveFocus;
    setOpenSectionKey(sectionKey);
    setHistoryVersion((v) => v + 1);
  };

  const toggleSection = (sectionKey, isOpen) => {
    replaceHistoryRef.current = true;
    navigateToSection(isOpen ? null : sectionKey, false);
  };

  const filtersBefore = (optionIndex) => {
    const nextFilters = {};
    options.slice(0, optionIndex).forEach(({ fieldId }) => {
      if (selectedFilters[fieldId] != null)
        nextFilters[fieldId] = selectedFilters[fieldId];
    });
    return nextFilters;
  };

  // A new field value can change the product, so later answers and accessories no longer apply.
  const selectValue = (optionIndex, option, value) => {
    if (fieldValuesEqual(selectedFilters[option.fieldId], value.fieldValue))
      return;
    commitUserSelection(
      { ...filtersBefore(optionIndex), [option.fieldId]: value.fieldValue },
      []
    );
  };

  const showSummary = () => {
    focusOpenedStepRef.current = true;
    setIsCompleted(true);
    window.scrollTo(0, 0);
    setHistoryVersion((v) => v + 1);
  };

  const goToNextStep = (currentIndex) => {
    const nextOption = options[currentIndex + 1];
    if (nextOption) {
      navigateToSection(fieldSectionKey(nextOption.fieldId));
    } else if (accessoryGroups.length > 0) {
      if (!pinnedProductId && filteredProducts.length > 0) {
        setPinnedProductId(filteredProducts[0].id);
      }
      navigateToSection(accessorySectionKey(0));
    } else {
      showSummary();
    }
  };

  const goToNextAccessoryGroup = (groupIndex) => {
    if (groupIndex + 1 < accessoryGroups.length) {
      navigateToSection(accessorySectionKey(groupIndex + 1));
    } else {
      showSummary();
    }
  };

  const confirmResetAndGoToStep = () => {
    if (pendingResetIndex === null) return;

    commitUserSelection(filtersBefore(pendingResetIndex), []);

    const targetOption = options[pendingResetIndex];
    focusOpenedStepRef.current = true;
    if (targetOption) setOpenSectionKey(fieldSectionKey(targetOption.fieldId));
    setPendingResetIndex(null);
  };

  const clearAllFilters = () => commitUserSelection({}, []);

  const handleUpdateQuantity = (accessory, newQuantity) => {
    const isSameAccessory = (item) => item.accessory.id === accessory.id;
    let nextAccessories;
    if (newQuantity <= 0) {
      nextAccessories = selectedAccessories.filter(
        (item) => !isSameAccessory(item)
      );
    } else if (selectedAccessories.some(isSameAccessory)) {
      nextAccessories = selectedAccessories.map((item) =>
        isSameAccessory(item) ? { ...item, quantity: newQuantity } : item
      );
    } else {
      nextAccessories = [
        ...selectedAccessories,
        { accessory, quantity: newQuantity },
      ];
    }
    commitUserSelection(selectedFilters, nextAccessories);
  };

  const handleCopyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const findValueTitle = (option, fieldValue) =>
    option.values.find((value) =>
      fieldValuesEqual(value.fieldValue, fieldValue)
    )?.title;

  const isOptionValueAvailable = (fieldId, fieldValue) =>
    filterProductsByFilters(products, {
      ...selectedFilters,
      [fieldId]: fieldValue,
    }).length > 0;

  const allSectionKeys = [
    ...options.map((o) => fieldSectionKey(o.fieldId)),
    ...accessoryGroups.map((_, index) => accessorySectionKey(index)),
  ];

  const activeSectionIndex = allSectionKeys.indexOf(openSectionKey);
  const isOptionDone = (option) =>
    selectedFilters[option.fieldId] != null ||
    isOptionSkippable(option, selectedFilters, products);
  const firstIncompleteIndex = options.findIndex(
    (option) => !isOptionDone(option)
  );
  const allOptionsDone = firstIncompleteIndex === -1;
  // With no section open, answered fields must still only be changeable through "edit".
  const frontierIndex =
    activeSectionIndex !== -1
      ? activeSectionIndex
      : allOptionsDone
      ? options.length
      : firstIncompleteIndex;
  const canNavigateAccessories =
    allOptionsDone || activeSectionIndex >= options.length;

  if (isCompleted) {
    return (
      <ConfiguratorSummary
        channelId={channelId}
        pageId={pageId}
        inquiryInfo={inquiryInfo}
        tosText={tosText}
        selectedProduct={selectedProduct}
        options={options}
        selectedFilters={selectedFilters}
        selectedAccessories={selectedAccessories}
        moveFocusOnOpen={focusOpenedStepRef.current}
        onBackToConfig={() => {
          focusOpenedStepRef.current = true;
          setOpenSectionKey(
            accessoryGroups.length > 0
              ? accessorySectionKey(accessoryGroups.length - 1)
              : fieldSectionKey(options[options.length - 1]?.fieldId)
          );
          setIsCompleted(false);
          setHistoryVersion((v) => v + 1);
        }}
        onReset={() => {
          focusOpenedStepRef.current = true;
          setIsCompleted(false);
          clearAllFilters();
          setOpenSectionKey(fieldSectionKey(options[0]?.fieldId));
        }}
      />
    );
  }

  return (
    <div className="configurator">
      {urlError && (
        <div className="configurator__url-error">
          <p>{translate('brenderup.configurator.url.invalid')}</p>
          <button
            type="button"
            onClick={() => setUrlError(false)}
            aria-label="Dismiss"
          >
          </button>
        </div>
      )}
      <div className="configurator__container">
        <div className="configurator__options">
          {options.map((option, optionIndex) => {
            const sectionKey = fieldSectionKey(option.fieldId);
            const isOpen = openSectionKey === sectionKey;
            const isLocked = optionIndex > frontierIndex;
            // Already-answered steps can only be revisited through "edit" (which warns about
            // resetting later answers) - expanding them directly must not change the selection.
            const isPastStep = optionIndex < frontierIndex;
            const selectedValue = selectedFilters[option.fieldId];
            const selectedValueTitle = selectedValue
              ? findValueTitle(option, selectedValue)
              : null;

            return (
              <ConfiguratorOptionStep
                key={option.fieldId}
                option={option}
                optionIndex={optionIndex}
                isOpen={isOpen}
                isLocked={isLocked}
                isPastStep={isPastStep}
                moveFocusOnOpen={focusOpenedStepRef.current}
                selectedValue={selectedValue}
                selectedValueTitle={selectedValueTitle}
                totalValuesCount={option.values.length}
                availableValuesCount={
                  option.values.filter((val) =>
                    isOptionValueAvailable(option.fieldId, val.fieldValue)
                  ).length
                }
                onToggle={() => {
                  if (isLocked || isPastStep) return;
                  toggleSection(sectionKey, isOpen);
                }}
                onRequestEdit={() => setPendingResetIndex(optionIndex)}
                onSelectValue={selectValue}
                onNextStep={() => goToNextStep(optionIndex)}
                checkIsAvailable={(val) =>
                  isOptionValueAvailable(option.fieldId, val)
                }
              />
            );
          })}

          {accessoryGroups.map((group, groupIndex) => {
            const sectionKey = accessorySectionKey(groupIndex);
            const isOpen = openSectionKey === sectionKey;
            const isLocked = !canNavigateAccessories;
            const isLastGroup = groupIndex === accessoryGroups.length - 1;
            const groupGlobalIndex = options.length + groupIndex;
            const isPastStep = !isOpen && activeSectionIndex > groupGlobalIndex;

            return (
              <ConfiguratorAccessoryGroup
                key={sectionKey}
                group={group}
                groupIndex={groupIndex}
                isOpen={isOpen}
                isLocked={isLocked}
                isPastStep={isPastStep}
                moveFocusOnOpen={focusOpenedStepRef.current}
                selectedAccessories={selectedAccessories}
                onToggle={() => {
                  if (isLocked) return;
                  toggleSection(sectionKey, isOpen);
                }}
                onUpdateQuantity={handleUpdateQuantity}
                onNextStep={() => goToNextAccessoryGroup(groupIndex)}
                isLastGroup={isLastGroup}
              />
            );
          })}
        </div>

        <div
          className={`configurator__selection ${
            isMobileSummaryOpen ? 'is-open' : ''
          }`}
        >
          <button
            className={`configurator__mobile-toggle-btn ${
              isMobileSummaryOpen ? 'rotate' : ''
            }`}
            onClick={() => setIsMobileSummaryOpen(!isMobileSummaryOpen)}
            aria-expanded={isMobileSummaryOpen}
          >
            {isMobileSummaryOpen ? (
              <>
                {translate('brenderup.configurator.hide.matching.products')} (
                {displayProducts.length})
              </>
            ) : (
              <>
                {translate('brenderup.configurator.show.matching.products')} (
                {displayProducts.length})
              </>
            )}
          </button>
          
          <div className="configurator__selection-content">
            <h2>{translate('brenderup.configurator.selected.products')}</h2>
            
            {displayProducts.map((product) => {
              const isSelected = product.id === selectedProduct?.id;

              return (
                <div key={product.systemId || product.id}>
                  <ConfiguratorProductCard
                    product={product}
                    isSelected={isSelected}
                    onSelect={
                      canSelectProduct
                        ? () => handleProductSelect(product)
                        : undefined
                    }
                    showSelectButton={canSelectProduct}
                  />

                  {isSelected && selectedAccessories.length > 0 && (
                    <div className="configurator__selected-accessories-container">
                      <p className="configurator__selected-accessories-title">
                        {translate(
                          'brenderup.configurator.selected.accessories'
                        )}
                      </p>
                      <div className="configurator__selected-accessories-list">
                        {selectedAccessories.map(({ accessory, quantity }) => {
                          const unitPrice = accessory.price ?? 0;
                          const totalPrice = unitPrice * quantity;
                          const formattedTotalPrice = `${numberFormat.format(totalPrice)} ${accessory.currencyCode}`;

                          return (
                            <div
                              className="configurator__selected-accessories-item"
                              key={accessory.id}
                            >
                              <p className="info">
                                <span className="name">
                                  {quantity} x {accessory.name}
                                </span>
                                <span className="price">{formattedTotalPrice}</span>
                              </p>
                              <p className="art-nr">
                                {translate('brenderup.configurator.product.art.nr')}{' '}
                                {accessory.id} {translate('brenderup.configurator.unit.price.prefix')} {accessory.formattedPrice || accessory.price}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                      <div className="configurator__selected-products-price desktop">
                        <p className="bold">
                          {translate('brenderup.configurator.price.total')}
                        </p>
                        <p>{numberFormat.format(totalPrice)} {selectedProduct.currencyCode}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            <div className="configurator__share-link">
              <p>{translate('brenderup.configurator.save.link.text')}</p>
              <button type="button" onClick={handleCopyLink}>
                {copied ? translate('brenderup.configurator.link.copied') : translate('brenderup.configurator.copy.link')}
              </button>
            </div>
          </div>

          {selectedProduct && (() => {
            const productName =
              selectedProduct.name || selectedProduct.title || '';
            const singleProductPrice = `${numberFormat.format(
              parsePrice(selectedProduct.price)
            )} ${selectedProduct.currencyCode}`;

            return (
              <div className="configurator__mobile-info-row">
                <h3>{productName}</h3>
                <div className="configurator__selected-products-price mobile">
                  {isAccessoryStep ? (
                    <>
                      <p className="bold">
                        {translate('brenderup.configurator.price.total')}
                      </p>
                      <p>{numberFormat.format(totalPrice)} {selectedProduct.currencyCode}</p>
                    </>
                  ) : (
                    <>
                      <p className="bold">
                        {translate('brenderup.configurator.price.from') ||
                          'Pris'}
                      </p>
                      <p>{singleProductPrice}</p>
                    </>
                  )}
                </div>
              </div>
            );
          })()}

        </div>
      </div>
      {pendingResetIndex !== null && (() => {
        const stepNumber = pendingResetIndex + 1;
        const stepTitle = options[pendingResetIndex]?.title || '';
        const popupTitle = `${translate('brenderup.configurator.popup.text.before')} ${stepNumber}${stepTitle ? ` (${stepTitle})` : ''} ${translate('brenderup.configurator.popup.text.after')}`;

        return (
          <ConfiguratorPopUp
            isOpen={pendingResetIndex !== null}
            onConfirm={confirmResetAndGoToStep}
            onCancel={() => setPendingResetIndex(null)}
            title={popupTitle}
            confirmText={translate('brenderup.configurator.popup.button.accept')}
            cancelText={translate('brenderup.configurator.popup.button.cancel')}
          />
        );
      })()}
    </div>
  );
};

export default ConfiguratorContainer;